import { CheckCircle, CheckCircleOutline, Edit } from "@mui/icons-material";
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { RenderedPreview } from "./renderedPreview";
import styles from "./styles.module.scss";
import { UnifiedDiffView } from "./unifiedDiffView";
import { useDiffLogic } from "./useDiffLogic";
import { useHighlight } from "./useHighlight";

interface DiffViewerProps {
  originalContent: string;
  updatedContent: string;
  className?: string;
  title?: string;
  description?: string;
  onApprovedDocumentChange?:
    | ((approvedDocument: string) => Promise<boolean | undefined> | boolean | undefined)
    | undefined;
  onClose?: () => void;
  compact?: boolean;
  renderHeaderExternally?: boolean;
  onHeaderChange?: (headerNode: React.ReactNode | null) => void;
}

export interface DiffViewerHandle {
  applyChanges: () => Promise<void>;
}

const DiffViewerComponent = (props: DiffViewerProps, ref: React.ForwardedRef<DiffViewerHandle>) => {
  const {
    originalContent,
    updatedContent,
    className = "",
    title,
    description,
    onApprovedDocumentChange,
    onClose,
    compact = false,
    renderHeaderExternally = false,
    onHeaderChange,
  } = props;

  const {
    diffResult,
    hunkData,
    setHunkData,
    hunkSelections,
    setHunkSelections,
    editingHunk,
    setEditingHunk,
    editedContent,
    setEditedContent,
    previewVersion,
    setPreviewVersion,
    hunkDisplayOrder,
    renderedPreviewHtml,
    getCurrentPreviewSourceText,
    handleSave,
    previousSelection,
    setPreviousSelection,
  } = useDiffLogic(originalContent, updatedContent, onApprovedDocumentChange, onClose);

  const [expandedHunks, setExpandedHunks] = useState<Set<number>>(new Set());
  const [approveAllChecked, setApproveAllChecked] = useState<boolean>(false);
  const showRenderedPreview = true;
  const [activeDisplayIndex, setActiveDisplayIndex] = useState<number | null>(null);
  const editTextareaRef = useRef<Map<number, HTMLTextAreaElement | null>>(new Map());
  const [previewWidth, setPreviewWidth] = useState<number>(50);
  const [isResizingPreview, setIsResizingPreview] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const approvedPreviewDocumentText = getCurrentPreviewSourceText();
  const originalPreviewDocumentText = getCurrentPreviewSourceText(); // This logic seems slightly redundant in hook, but re-using

  const { previewBodyRef, handleCardActivate } = useHighlight(
    showRenderedPreview,
    activeDisplayIndex,
    setActiveDisplayIndex,
    previewVersion,
    approvedPreviewDocumentText,
    originalPreviewDocumentText,
    renderedPreviewHtml,
  );

  const showNums = false;
  const isWrapped = true;

  const handlePreviewResizeMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!showRenderedPreview) return;
    event.preventDefault();
    setIsResizingPreview(true);
  }, []);

  useEffect(() => {
    if (!isResizingPreview || !containerRef.current) {
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      if (rect.width <= 0) return;

      const newPreviewWidth = ((rect.right - event.clientX) / rect.width) * 100;
      if (!Number.isFinite(newPreviewWidth)) {
        return;
      }

      const clampedWidth = Math.min(80, Math.max(20, newPreviewWidth));
      setPreviewWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizingPreview(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };
  }, [isResizingPreview]);

  useEffect(() => {
    if (!showRenderedPreview && isResizingPreview) {
      setIsResizingPreview(false);
    }
  }, [isResizingPreview]);

  // 全ハンクを展開
  const expandAll = useCallback(() => {
    setExpandedHunks(new Set(diffResult.hunks.map((_, i) => i)));
  }, [diffResult.hunks]);

  // ハンク単位での選択処理
  const handleHunkSelection = useCallback(
    (hunkIndex: number, selection: "original" | "updated") => {
      setHunkSelections((prev) => {
        const next = new Map(prev);
        next.set(hunkIndex, selection);
        return next;
      });
      // 編集モードを終了
      setEditingHunk(null);
      // 編集内容は保持する（リセットしない）
    },
    [setHunkSelections, setEditingHunk],
  );

  // ハンク選択のリセット処理
  const handleHunkReset = useCallback(
    (hunkIndex: number) => {
      setHunkSelections((prev) => {
        const next = new Map(prev);
        next.delete(hunkIndex);
        return next;
      });
    },
    [setHunkSelections],
  );

  // 編集開始（after/更新後のみ編集可能）
  const handleStartEdit = useCallback(
    (hunkIndex: number) => {
      const hunk = diffResult.hunks[hunkIndex];
      if (!hunk) return;

      handleCardActivate(hunkDisplayOrder.get(hunkIndex) ?? hunkIndex);

      const currentSelection = hunkSelections.get(hunkIndex);
      const currentHunkData = hunkData.get(hunkIndex);

      // 編集前の選択状態を保存（キャンセル時に復元するため）
      setPreviousSelection(currentSelection || null);

      // 編集開始時に自動的に承認状態（updated）に切り替え
      setHunkSelections((prev) => {
        const next = new Map(prev);
        next.set(hunkIndex, "updated");
        return next;
      });

      // afterの内容を編集エリアに設定
      const afterContent = currentHunkData?.targetUpdated || "";
      setEditedContent(afterContent);
      setEditingHunk(hunkIndex);
    },
    [
      diffResult,
      hunkSelections,
      hunkData,
      hunkDisplayOrder,
      setPreviousSelection,
      setHunkSelections,
      setEditedContent,
      setEditingHunk,
      handleCardActivate,
    ],
  );

  // 編集完了処理（afterのみ更新）
  const handleSaveEdit = useCallback(() => {
    if (editingHunk === null) return;

    // 編集内容をafterに保存
    setHunkData((prev) => {
      const next = new Map(prev);
      const currentData = next.get(editingHunk);

      if (currentData) {
        const updatedData = { ...currentData };
        updatedData.targetUpdated = editedContent;
        next.set(editingHunk, updatedData);
      }

      return next;
    });

    // 編集モードを終了し、編集前の選択状態をクリア
    setEditingHunk(null);
    setEditedContent("");
    setPreviousSelection(null);
  }, [
    editingHunk,
    editedContent,
    setHunkData,
    setEditingHunk,
    setEditedContent,
    setPreviousSelection,
  ]);

  // 編集キャンセル処理
  const handleCancelEdit = useCallback(() => {
    if (editingHunk === null) return;

    // 編集前の選択状態に戻す
    setHunkSelections((prev) => {
      const next = new Map(prev);
      if (previousSelection !== null) {
        next.set(editingHunk, previousSelection);
      } else {
        // 元々未選択だった場合は選択を削除
        next.delete(editingHunk);
      }
      return next;
    });

    // 編集モードを終了
    setEditingHunk(null);
    setEditedContent("");
    setPreviousSelection(null);
  }, [
    editingHunk,
    previousSelection,
    setHunkSelections,
    setEditingHunk,
    setEditedContent,
    setPreviousSelection,
  ]);

  const adjustTextareaHeight = useCallback(
    (hunkIndex: number | null, textarea?: HTMLTextAreaElement | null) => {
      if (hunkIndex === null) return;
      const target = textarea ?? editTextareaRef.current.get(hunkIndex);
      if (!target) return;
      target.style.height = "auto";
      target.style.height = `${target.scrollHeight}px`;
    },
    [],
  );

  useLayoutEffect(() => {
    adjustTextareaHeight(editingHunk);
  }, [adjustTextareaHeight, editingHunk]);

  const registerTextareaRef = useCallback(
    (hunkIndex: number, element: HTMLTextAreaElement | null) => {
      if (element) {
        editTextareaRef.current.set(hunkIndex, element);
        adjustTextareaHeight(hunkIndex, element);
      } else {
        editTextareaRef.current.delete(hunkIndex);
      }
    },
    [adjustTextareaHeight],
  );

  // 編集オーバーレイをレンダリングするヘルパー関数
  const renderEditOverlay = useCallback(
    (hunkIndex: number, hasChanges: boolean) => {
      const selection = hunkSelections.get(hunkIndex);
      const canEdit = hasChanges && selection === "updated";

      if (!hasChanges) return null;

      const label = canEdit ? "クリックして編集" : "承認して編集";

      const handleClick = (event: React.MouseEvent | React.KeyboardEvent) => {
        if (canEdit) return;
        event.stopPropagation();
        handleStartEdit(hunkIndex);
      };

      const handleKeyDown = (event: React.KeyboardEvent) => {
        if (canEdit) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleStartEdit(hunkIndex);
        }
      };

      return (
        <div className={styles["editOverlay"]}>
          <div
            className={`${styles["editOverlayContent"]} ${!canEdit ? styles["editOverlayAction"] : ""}`}
            onClick={!canEdit ? handleClick : undefined}
            onKeyDown={!canEdit ? handleKeyDown : undefined}
            role={!canEdit ? "button" : undefined}
            tabIndex={!canEdit ? 0 : undefined}
          >
            <Edit sx={{ fontSize: "1rem" }} />
            <span className={styles["editOverlayText"]}>{label}</span>
          </div>
        </div>
      );
    },
    [hunkSelections, handleStartEdit],
  );

  // 選択統計の計算
  const selectionStats = useMemo(() => {
    const totalChangedHunks = diffResult.hunks.filter((hunk) =>
      hunk.lines.some((l) => l.type !== "context"),
    ).length;

    const approvedHunks = Array.from(hunkSelections.values()).filter(
      (selection) => selection === "updated",
    ).length;

    const resolvedHunks = hunkSelections.size;

    return { approved: approvedHunks, resolved: resolvedHunks, total: totalChangedHunks };
  }, [diffResult, hunkSelections]);

  const { approved: approvedCount, resolved: resolvedCount, total: totalCount } = selectionStats;

  useEffect(() => {
    const shouldBeChecked = totalCount > 0 && approvedCount === totalCount;
    if (approveAllChecked !== shouldBeChecked) {
      setApproveAllChecked(shouldBeChecked);
    }
  }, [approvedCount, totalCount, approveAllChecked]);

  // 全て承認操作の処理
  const handleApproveAllCheckboxChange = useCallback(
    (checked: boolean) => {
      setApproveAllChecked(checked);
      if (checked) {
        // すべての差分をupdatedに設定
        const newSelections = new Map<number, "original" | "updated">();
        diffResult.hunks.forEach((hunk, index) => {
          const hasChanges = hunk.lines.some((l) => l.type !== "context");
          if (hasChanges) {
            newSelections.set(index, "updated");
          }
        });
        setHunkSelections(newSelections);
      } else {
        // チェックを外した場合は全て選択をクリア（未選択状態に戻す）
        setHunkSelections(new Map());
      }
    },
    [diffResult, setHunkSelections],
  );

  const handleApproveAllClick = useCallback(() => {
    const shouldApproveAll = !(totalCount > 0 && approvedCount === totalCount);
    handleApproveAllCheckboxChange(shouldApproveAll);
  }, [approvedCount, totalCount, handleApproveAllCheckboxChange]);

  useEffect(() => {
    if (activeDisplayIndex !== null) {
      return;
    }
    const firstEntry = hunkDisplayOrder.values().next();
    if (!firstEntry.done) {
      setActiveDisplayIndex(firstEntry.value);
    }
  }, [hunkDisplayOrder, activeDisplayIndex]);

  useImperativeHandle(
    ref,
    () => ({
      applyChanges: handleSave,
    }),
    [handleSave],
  );

  const headerElement = useMemo(() => null, []);

  useEffect(() => {
    if (!onHeaderChange) return;

    if (renderHeaderExternally) {
      onHeaderChange(headerElement);
      return () => onHeaderChange(null);
    }

    onHeaderChange(null);
    return undefined;
  }, [renderHeaderExternally, onHeaderChange, headerElement]);

  // コンパクトモードの初期設定
  useEffect(() => {
    if (compact) {
      // コンパクトモードでは最初のハンクのみ展開
      setExpandedHunks(new Set([0]));
    } else {
      // 通常モードでは全ハンク展開
      expandAll();
    }
  }, [compact, expandAll]);

  const containerClassName = [
    styles["diffViewerContainer"],
    compact ? styles["compact"] : "",
    renderHeaderExternally ? styles["modalLayout"] : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  const containerStyle = renderHeaderExternally ? { height: "100%" } : undefined;

  // 差分がない場合
  if (!diffResult.hasChanges) {
    return (
      <div className={containerClassName} style={containerStyle}>
        {title && (
          <div className={styles["diffHeader"]}>
            <h3 className={styles["title"]}>{title}</h3>
            {description && <p className={styles["description"]}>{description}</p>}
          </div>
        )}
        <div className={styles["noChanges"]}>
          <CheckCircle className={styles["icon"] || ""} />
          <span>変更はありません</span>
        </div>
      </div>
    );
  }

  const diffPaneStyle = showRenderedPreview
    ? { width: `${100 - previewWidth}%` }
    : { width: "100%" };
  const previewPaneStyle = { width: `${previewWidth}%` };
  const previewHasContent = getCurrentPreviewSourceText().trim().length > 0;
  const isEditing = editingHunk !== null;

  return (
    <div className={containerClassName} style={containerStyle}>
      {/* タイトル・説明セクション */}
      {title && (
        <div className={styles["diffHeader"]}>
          <h3 className={styles["title"]}>{title}</h3>
          {description && <p className={styles["description"]}>{description}</p>}
        </div>
      )}

      {/* ヘッダー部分 */}
      {!renderHeaderExternally && headerElement}

      <div
        ref={containerRef}
        className={`${styles["contentSplit"]} ${renderHeaderExternally ? styles["contentSplitNoHeader"] : ""}`}
      >
        {/* 差分表示部分 */}
        <div className={styles["diffContent"]} style={diffPaneStyle}>
          <div className={styles["diffContentHeader"]}>
            <div className={styles["diffContentHeaderLeft"]}>
              <h4>差分一覧</h4>
            </div>
            <div className={styles["diffContentHeaderRight"]}>
              <span className={styles["diffContentCounter"]}>
                対応済み {resolvedCount}/{totalCount}
              </span>
              <button
                type="button"
                className={`${styles["approveAllButton"]} ${approveAllChecked ? styles["approveAllButtonActive"] : ""}`}
                onClick={handleApproveAllClick}
                disabled={isEditing || totalCount === 0}
              >
                <CheckCircleOutline
                  sx={{
                    fontSize: "1rem",
                    color: isEditing || totalCount === 0 ? "#B9B6AC" : "#1A8FC1",
                  }}
                />
                {approveAllChecked ? "承認を解除" : "すべて承認"}
              </button>
            </div>
          </div>
          <div
            className={`${styles["diffContentBody"]} ${isWrapped ? styles["wrap"] : styles["nowrap"]}`}
          >
            <UnifiedDiffView
              diffResult={diffResult}
              showLineNumbers={showNums}
              compact={compact}
              expandedHunks={expandedHunks}
              hunkSelections={hunkSelections}
              onHunkSelection={handleHunkSelection}
              onHunkReset={handleHunkReset}
              editingHunk={editingHunk}
              editedContent={editedContent}
              setEditedContent={setEditedContent}
              onStartEdit={handleStartEdit}
              onSaveEdit={handleSaveEdit}
              onCancelEdit={handleCancelEdit}
              hunkData={hunkData}
              renderEditOverlay={renderEditOverlay}
              activeDisplayIndex={activeDisplayIndex}
              onCardActivate={handleCardActivate}
              hunkDisplayOrder={hunkDisplayOrder}
              registerTextareaRef={registerTextareaRef}
            />
          </div>
        </div>
        <RenderedPreview
          showRenderedPreview={showRenderedPreview}
          previewVersion={previewVersion}
          setPreviewVersion={setPreviewVersion}
          isEditing={isEditing}
          previewHasContent={previewHasContent}
          renderedPreviewHtml={renderedPreviewHtml}
          previewPaneStyle={previewPaneStyle}
          previewBodyRef={previewBodyRef}
          handlePreviewResizeMouseDown={handlePreviewResizeMouseDown}
        />
      </div>
    </div>
  );
};

const DiffViewer = React.forwardRef<DiffViewerHandle, DiffViewerProps>(DiffViewerComponent);

DiffViewer.displayName = "DiffViewer";

export default DiffViewer;
